import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useCommunityContext } from "@voxmedia/duet-context/context/community-context";
import { useGlobalDataContext } from "@voxmedia/duet-context/context/global-data-context";
import NavigationDrawer from "@voxmedia/duet-ui/components/navigation/Navigation/components/NavigationDrawer";
import type { NavigationDrawerConfig } from "@voxmedia/duet-ui/components/navigation/Navigation/Navigation";
import { useComponentConfig } from "@voxmedia/duet-ui/context/component-config-context";
import { useNavigationContext } from "@voxmedia/duet-ui/context/navigation-context";
import useDefaults from "@voxmedia/duet-ui/hooks/use-defaults";
import CskMinimalNav from "src/components/CskMinimalNav";

interface Props {
  getDrawerComponents?: (origin: string) => NavigationDrawerConfig;
  minimalNavClassName?: string;
}

/**
 * CSK story-shell navigation: minimal bar + slide-out drawer. Uses CskMinimalNav
 * (comments, not notifications) and CSK drawer label overrides via component config.
 */
const CskStoryNavigation: FunctionComponent<Props> = (props) => {
  const componentConfig = useComponentConfig("navigation");
  const { Logo, getDrawerComponents, getDrawerItems, drawerItems, showAuth } =
    useDefaults(props, componentConfig);

  const { currentOrigin } = useCommunityContext();
  const { setOpenDrawerFn, setCloseDrawerFn } = useNavigationContext();
  const { mainNav } = useGlobalDataContext();

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [initialSearchFocus, setInitialSearchFocus] = useState(false);

  const openDrawer = useCallback(
    (options: { initialSearchFocus?: boolean } = {}) => {
      setInitialSearchFocus(Boolean(options.initialSearchFocus));
      setDrawerOpen(true);
    },
    [],
  );
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  useEffect(() => {
    setOpenDrawerFn(openDrawer);
    setCloseDrawerFn(closeDrawer);
  }, [closeDrawer, openDrawer, setCloseDrawerFn, setOpenDrawerFn]);

  const drawer = useMemo(
    () => getDrawerComponents(currentOrigin),
    [getDrawerComponents, currentOrigin],
  );

  const items = useMemo(
    () =>
      getDrawerItems ? getDrawerItems(currentOrigin, mainNav) : drawerItems,
    [currentOrigin, drawerItems, getDrawerItems, mainNav],
  );

  return (
    <>
      <CskMinimalNav Logo={Logo} className={props.minimalNavClassName} />
      <NavigationDrawer
        items={items}
        open={isDrawerOpen}
        onClose={closeDrawer}
        shortcutBar={drawer.shortcutBar}
        endSection={drawer.endSection}
        background={drawer.background}
        randomizeSearchPlaceholder={drawer.randomizeSearchPlaceholder}
        showAuth={showAuth}
        initialSearchFocus={initialSearchFocus}
      />
    </>
  );
};

export default CskStoryNavigation;
